import type { OpenerDescalceRow } from '../../api/useOpenerDescalce';
import { residualIsReal } from './descalceResidual';
import { hasUnreadLeg, legCount } from './descalceLegs';

/**
 * PURE comparator for the Descalce screen table. No React here — see
 * `descalceSort.test.ts` for the mutation-tested guards.
 *
 * Ranking rule for the `descalce` column. Deliberately NO LONGER a mirror of
 * `live/venuePositions.ts` → `venueSortMagnitude`, which still ranks every
 * resolving row at `0`; this screen has the `resolvingVenue` the opener page
 * does not:
 *   - `venue === null`, an UNREAD leg (no `descalce` to rank at all),
 *     `ambiguous`, or `marketsIncomplete` → `Infinity`. In none of the four is
 *     the number a real cross-venue descalce, and an unknown must lead rather
 *     than sink into the flat majority.
 *   - a resolving row whose residual is NOT trustworthy (see `residualIsReal`)
 *     → `0`, the old artefact handling.
 *   - otherwise → `venue.descalce` (already `|signedNet|`). A resolving row
 *     with a trustworthy residual ranks by magnitude like any naked leg:
 *     burying it would break the screen's "largest naked leg first" promise.
 * Ties break A→Z on `pairName`.
 */
export function descalceRankMagnitude(row: OpenerDescalceRow): number {
  if (
    row.venue == null ||
    row.venue.descalce == null ||
    hasUnreadLeg(row) ||
    row.venue.ambiguous === true ||
    row.venue.marketsIncomplete === true
  ) {
    return Number.POSITIVE_INFINITY;
  }
  if (row.venue.resolving === true && !residualIsReal(row.venue)) return 0;
  return Number.isFinite(row.venue.descalce)
    ? row.venue.descalce
    : Number.POSITIVE_INFINITY;
}

export type DescalceSortColumn =
  | 'descalce'
  | 'pairName'
  | 'group'
  | 'polyYes'
  | 'polyNo'
  | 'kalshiYes'
  | 'kalshiNo'
  | 'belief'
  | 'divergence'
  | 'ruleStatus'
  | 'triage';

export type DescalceSortDirection = 'asc' | 'desc';

/**
 * The `triage` column ranks by a count the snapshot row does not carry — it
 * lives in the triage index — so the lookup is injected. Absent, every row
 * ranks `null` and the table falls back to A→Z, which is what an unreadable
 * index should look like: no order invented out of counts nobody could read.
 */
export interface DescalceSortLookup {
  triageCount?: (row: OpenerDescalceRow) => number;
}

export interface DescalceSortState {
  column: DescalceSortColumn;
  direction: DescalceSortDirection;
}

export const DEFAULT_DESCALCE_SORT: DescalceSortState = {
  column: 'descalce',
  direction: 'desc'
};

/** Value used to sort a non-`descalce` column; `null` always sorts last. */
function secondaryColumnValue(
  row: OpenerDescalceRow,
  column: DescalceSortColumn,
  lookup?: DescalceSortLookup
): number | string | null {
  switch (column) {
    case 'pairName':
      return (row.pairName ?? '').toLowerCase();
    case 'group':
      return (row.group.name ?? 'ungrouped').toLowerCase();
    // Through `legCount`, the same gate the CELLS use: a payload can carry the
    // previous sweep's counts next to `legsRead[leg] = false`, and a number
    // that renders as `—` must not be the number the row is ranked by.
    case 'polyYes':
      return legCount(row, 'polymarket', row.venue?.polyYes);
    case 'polyNo':
      return legCount(row, 'polymarket', row.venue?.polyNo);
    case 'kalshiYes':
      return legCount(row, 'kalshi', row.venue?.kalshiYes);
    case 'kalshiNo':
      return legCount(row, 'kalshi', row.venue?.kalshiNo);
    case 'belief':
      return row.belief?.descalce ?? null;
    case 'divergence':
      return row.divergence != null ? Math.abs(row.divergence.total) : null;
    case 'ruleStatus':
      return (row.ruleStatus ?? '').toLowerCase();
    case 'triage': {
      // `0` is reported as `null` so an unmarked row sorts LAST in both
      // directions: ascending on this column means "show me the marked ones,
      // fewest first", never "bury them under a page of clean rows".
      const marks = lookup?.triageCount?.(row) ?? 0;
      return marks > 0 ? marks : null;
    }
    default:
      return null;
  }
}

const pairNameAsc = (a: OpenerDescalceRow, b: OpenerDescalceRow): number =>
  (a.pairName ?? '').localeCompare(b.pairName ?? '');

export function compareDescalceRows(
  a: OpenerDescalceRow,
  b: OpenerDescalceRow,
  sort: DescalceSortState = DEFAULT_DESCALCE_SORT,
  lookup?: DescalceSortLookup
): number {
  if (sort.column === 'descalce') {
    const magA = descalceRankMagnitude(a);
    const magB = descalceRankMagnitude(b);
    if (magA !== magB) {
      return sort.direction === 'desc' ? magB - magA : magA - magB;
    }
    return pairNameAsc(a, b);
  }

  const va = secondaryColumnValue(a, sort.column, lookup);
  const vb = secondaryColumnValue(b, sort.column, lookup);
  if (va == null && vb == null) return pairNameAsc(a, b);
  if (va == null) return 1;
  if (vb == null) return -1;
  if (va !== vb) {
    if (typeof va === 'number' && typeof vb === 'number') {
      return sort.direction === 'desc' ? vb - va : va - vb;
    }
    const cmp = String(va).localeCompare(String(vb));
    return sort.direction === 'desc' ? -cmp : cmp;
  }
  return pairNameAsc(a, b);
}

export function sortDescalceRows(
  rows: OpenerDescalceRow[],
  sort: DescalceSortState = DEFAULT_DESCALCE_SORT,
  lookup?: DescalceSortLookup
): OpenerDescalceRow[] {
  return [...rows].sort((a, b) => compareDescalceRows(a, b, sort, lookup));
}
